<template>
  <ul v-if="isVisible" :class="classes">
    <li
      v-for="(suggestion, index) in suggestions"
      :key="index"
      :class="{ selected: index === selected }"
      class="suggestion-item"
      data-value="ccc.edu"
      data-test-id="suggestion-item"
      @mouseover="setSelected(index)"
      @focus="setSelected(index)"
    >
      <a
        v-safe-html="markHtml(suggestion)"
        class="suggestion-link"
        href="#"
        tabindex="0"
        data-test-id="suggestion-link"
        rel="noopener"
        @click="triggerSearch(suggestion)"
      />
    </li>
  </ul>
</template>

<script>
import { stringify } from 'qs';

import { createMarkHtml } from '~~/common/js/universal/utils';
import fetch, { AbortController } from '~~/common/js/universal/fetch';
import classes from '~~/common/vue/mixins/classes';

let cache = {};
let cancelRequest = () => {};

export const variants = ['default', 'compact'];

export default {
  name: 'ESuggestionList',
  mixins: [classes],
  props: {
    inputText: {
      type: String,
      default: '',
    },
    autocompleteUrl: {
      type: String,
      required: true,
    },
    market: {
      type: String,
      default: 'en-us',
    },
    topSectionOnly: {
      type: Boolean,
      default: true,
    },
    /**
     * One of `default, compact`
     */
    variant: {
      type: String,
      cssClass: true,
      default: 'default',
      validator: (variant) => variants.includes(variant),
    },
  },
  data() {
    return {
      suggestions: [],
      limit: this.topSectionOnly ? 3 : 5,
      selected: -1,
    };
  },
  computed: {
    markHtml() {
      return createMarkHtml(this.inputText, 'suggestion-highlight', 'suggestion-highlight');
    },
    isVisible() {
      return this.suggestions.length > 0;
    },
  },
  watch: {
    inputText: 'fetchSuggestions',
    isVisible(visible) {
      this.$emit('visibilityChange', visible);
    },
  },
  mounted() {
    this.$parent.$on('clearSuggestions', () => this.clearSuggestions());
    this.$parent.$on('getSuggestions', () => this.fetchSuggestions(this.inputText));
    this.$parent.$on('onKeyDown', (key) => {
      if (key === 'esc') {
        this.clearSuggestions();
      }
      if (key === 'up') {
        this.selected -= 1;
      } else if (key === 'down') {
        this.selected += 1;
      } else {
        return;
      }
      if (this.selected > this.suggestions.length) {
        this.selected = 0;
      } else if (this.selected <= -1) {
        this.selected = this.suggestions.length;
      }
      this.$emit('updateInputText', this.suggestions[this.selected]);
    });
  },
  methods: {
    clearSuggestions() {
      this.selected = -1;
      this.suggestions = [];
    },
    clearCache() {
      cache = {};
    },
    getSuggestions(query, market) {
      const controller = new AbortController();
      const url = `${this.autocompleteUrl}?${stringify({ q: query, mkt: market })}`;
      const request = fetch(url, { signal: controller.signal });
      const cancel = () => controller.abort();
      const result = request.then((resp) => resp.json());
      return { cancel, result };
    },
    async fetchSuggestions(text) {
      this.selected = -1;
      cancelRequest();
      if (!text) {
        this.clearSuggestions();
        return;
      }
      if (cache[text]) {
        this.suggestions = cache[text];
        return;
      }
      try {
        const { result, cancel } = await this.getSuggestions(text, this.market);
        cancelRequest = cancel;
        const { suggestions } = await result;
        if (suggestions.length === 0) {
          return;
        }
        // TODO: verify wether limit can be passed to backend
        this.suggestions = suggestions.slice(0, this.limit);
        cache[text] = this.suggestions;
      } catch (err) {
        // Do nothing
      }
    },
    setSelected(selected) {
      this.selected = selected;
    },
    triggerSearch(suggestion) {
      this.clearSuggestions();
      this.$emit('triggerSuggestionSearch', suggestion);
    },
  },
};
</script>


<style lang="scss" scoped>
.suggestion-list {
  position: absolute;
  top: 100%;
  left: 0;
  width: calc(100% + 2px);
  z-index: $z-index-3l;
  padding-left: 0;
  margin: 0 0 $space-s -1px;
  text-align: left;
  list-style-type: none;
  background-color: $white;
  border: solid $border-width $color-border;
  border-bottom-right-radius: $border-radius-s;
  border-bottom-left-radius: $border-radius-s;
  box-shadow: 0 1px 1px 0 $color-elevation-m;
  font-size: $font-l;

  @include tablet {
    font-size: $font-l;
  }
}

.suggestion-item {
  padding-left: 0;
  color: $color-text-primary;

  &.selected {
    a {
      color: $color-text-primary;
    }
  }

  &:hover,
  &:active,
  &.selected {
    background: $color-highlight-primary;
  }
}

.suggestion-link {
  display: block;
  width: 100%;
  padding: $space-s;
  font-weight: $font-weight-1l;
  line-height: $line-height-s;
  color: $color-text-primary;
  text-decoration: none;
  cursor: pointer;
  max-width: 100%;
  box-sizing: border-box;

  &:hover,
  &:visited,
  &:active {
    color: $color-text-primary;
    text-decoration: none;
  }

  @include mobile-l {
    padding: $space-m;
  }

  @include tablet {
    padding: $space-s $space-l;

    .suggestion-list--variant-compact & {
      padding-left: $space-m;
    }
  }
}
</style>

<style lang="scss">
.suggestion-highlight {
  font-weight: $font-weight-s;
  color: inherit;
  background: $transparent;
}
</style>


<docs>
Default

```js
let inputText = ""
<div :style='{position: "relative" }'>
  <EInput v-model="inputText" />
  <ESuggestionList :input-text="inputText" autocomplete-url="https://ac.ecosia.org'" />
</div>
```

</docs>
